import { graphql, useStaticQuery } from 'gatsby';
import qs from 'query-string';

import { useCampaignContext } from '../CampaignProvider';
import type { SearchFilters } from '../model/SearchFilters';
import type { Language } from '../services/Language';
import { useTranslationContext } from '../TranslationProvider';

export interface CampaignContent {
  utm: string;
  vehicle_tag: string;
  header: string;
  subheader: string;
  button?: string;
  image: string;
  locale: Language;
  filters?: { name: string; value: string }[];
  heroButtons?: {
    text: string;
    url: string;
    order: number;
    className?: string;
    icon?: {
      side: 'left' | 'right';
      type: string;
    };
  }[];
}

export interface UseCampaignContent extends CampaignContent {
  filtersQuery: string | undefined;
}

interface QueryData {
  dynamicPages?: {
    nodes?: CampaignContent[];
  };
}

export function useCampaignContent(): UseCampaignContent {
  const data = useStaticQuery<QueryData>(graphql`
    query {
      dynamicPages: allStrapiDynamicHomepage(filter: { enabled: { eq: true } }) {
        nodes {
          utm
          vehicle_tag
          header
          subheader
          button
          image
          locale
          filters {
            name
            value
          }
          heroButtons {
            text
            url
            order
            className
            icon {
              side
              type
            }
          }
        }
      }
    }
  `);
  const { language } = useTranslationContext();
  const campaign = useCampaignContext();

  if (!campaign.id) return null;
  const content = (data.dynamicPages?.nodes ?? []).find(it => {
    return it.locale === language && it.utm === campaign.id;
  });
  if (!content) return null;

  const filters: Partial<SearchFilters> = {};
  for (const { name, value } of content.filters) {
    if (name in filters) {
      filters[name] = [filters[name], value];
    } else {
      filters[name] = value;
    }
  }
  const filtersQuery = Object.keys(filters).length ? qs.stringify(filters) : undefined;
  return { ...content, filtersQuery };
}
